import { SelectionArrangeControls } from './SelectionArrangeControls';
import { AtomContextControls, AtomPropertyPopover, type AtomPanel } from './AtomContextControls';
import { PeriodicTablePanel } from './PeriodicTablePanel';
import { useRef, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { SelectionAppearancePopover, SelectionContextControls, selectionContext } from './SelectionContextControls';
import { ToolbarMenu } from './ToolbarMenu';
import type { ImportTarget } from './FileDrawer';
import { hasSelection } from '@peruselab/structure-editor/domain/editor';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../components/Icon';
import { PointerModeToggle } from '../../components/PointerModeToggle';
import { MoreMenu, type MoreMenuAction } from './MoreMenu';
import type { ToolbarGroup } from './useToolbarOverflow';
import type { UseDrawingDocumentResult } from '../../hooks/useDrawingDocument';
import type { usePartialCharges } from '../../hooks/usePartialCharges';
import { useOutsideLookupsAllowed } from '../../tenancy/activeTenant';

interface EditorActionBarProps {
  readonly onImport: (target: ImportTarget) => void;
  readonly onSave: () => void;
  readonly saveVisible: boolean;
  readonly saveBusy: boolean;
  /** #1051: the user's role where the drawing would be saved is view-only -- Save is shown, disabled. */
  readonly saveReadOnly?: boolean;
  readonly onExportImage: () => void;
  readonly onExportJson: () => void;
  readonly onPrint: () => void;
  readonly printBusy: boolean;
  readonly doc: UseDrawingDocumentResult;
  /** #1078 -- the Atom group shows (or computes) the selected atom's partial charge. */
  readonly partialCharges: ReturnType<typeof usePartialCharges>;
  readonly onAutoLayout: () => void;
  readonly autoLayoutBusy: boolean;
  // #807 (owner) -- the File drawer's folder button leads the row, before Undo.
  readonly onOpenFile: () => void;
  readonly fileButtonRef: RefObject<HTMLButtonElement | null>;
  /** #807 part 1b -- where the paper's own view controls (Pen-only, zoom, snapping, page) go: after
   *  the pointer modes, before Auto layout and ⋯. */
  readonly viewControls: ReactNode;
  /** #807 R4 -- groups with no room on the row: hidden here and offered in ⋯ instead. */
  readonly hiddenGroups: ReadonlySet<ToolbarGroup>;
  /** The ⋯ menu, controlled by the shell (the paper's overflowed view controls close it too). */
  readonly moreOpen: boolean;
  readonly onMoreOpenChange: (open: boolean) => void;
  readonly onMoreTrigger: (element: HTMLButtonElement | null) => void;
  readonly overflowSlotRef: (element: HTMLDivElement | null) => void;
}

// ADR 0007 P4 — the thin action strip above the paper. #807 (owner-approved mockup): one flat row,
// grouped by job -- File | Undo, Redo | selection actions | Draw, Select, Pan | the paper's view controls (portalled in
// by DrawingPaper) | Auto layout | ⋯. ⋯ holds Clear drawing and, under "Coming soon", 3D view and
// Lock (owner: the other placeholders are dropped).
export function EditorActionBar({
  doc,
  partialCharges,
  onImport,
  onSave,
  saveVisible,
  saveBusy,
  saveReadOnly = false,
  onExportImage,
  onExportJson,
  onPrint,
  printBusy,
  onAutoLayout,
  autoLayoutBusy,
  onOpenFile,
  fileButtonRef,
  viewControls,
  hiddenGroups,
  moreOpen,
  onMoreOpenChange,
  onMoreTrigger,
  overflowSlotRef
}: EditorActionBarProps) {
  const outsideLookupsAllowed = useOutsideLookupsAllowed();
  const { t } = useTranslation();
  const { history, undo, redo, clearDrawing, document, tool, setTool } = doc;
  const appearanceAnchor = useRef<HTMLElement | null>(null);
  const [appearance, setAppearance] = useState<'color' | 'width' | null>(null);
  const context = selectionContext(doc);
  const openAppearance = (kind: 'color' | 'width', anchor: HTMLElement | null) => {
    appearanceAnchor.current = anchor;
    setAppearance(kind);
  };
  // #1078 -- the Atom group's periodic table / popovers; tied to the atom they were opened for.
  const atomAnchor = useRef<HTMLElement | null>(null);
  const [atomPanel, setAtomPanel] = useState<{ readonly panel: AtomPanel; readonly atomId: string } | null>(null);
  const openAtomPanel = (panel: AtomPanel, anchor: HTMLElement | null) => {
    if (!doc.singleAtomId) return;
    atomAnchor.current = anchor;
    setAtomPanel({ panel, atomId: doc.singleAtomId });
  };
  const openAtom = atomPanel && atomPanel.atomId === doc.singleAtomId ? atomPanel.panel : null;
  const hasNodes = document.structure.graph.nodes.length > 0;
  const hasContent = hasNodes || document.annotations.length > 0;

  const importActions: readonly MoreMenuAction[] = [
    { key: 'json', icon: 'uploadFile', label: t('workspace.fileFormats.importJson'), onSelect: () => onImport('json') },
    { key: 'smiles', icon: 'polyline', label: t('smilesImportPanel.importButton'), onSelect: () => onImport('smiles') },
    {
      key: 'markush',
      icon: 'polyline',
      label: t('markushImportPanel.importButton'),
      onSelect: () => onImport('markush')
    },
    // #1064: PubChem and image import send things to outside services -- not where the
    // organisation has turned outside lookups off.
    ...(outsideLookupsAllowed
      ? ([
          {
            key: 'pubchem',
            icon: 'search',
            label: t('pubChemImportPanel.importButton'),
            onSelect: () => onImport('pubchem')
          },
          {
            key: 'image',
            icon: 'image',
            label: t('workspace.actionBar.importImage'),
            onSelect: () => onImport('image')
          }
        ] satisfies readonly MoreMenuAction[])
      : [])
  ];
  const exportActions: readonly MoreMenuAction[] = [
    { key: 'image', icon: 'image', label: t('workspace.actionBar.exportImage'), onSelect: onExportImage },
    { key: 'json', icon: 'download', label: t('workspace.fileFormats.exportJson'), onSelect: onExportJson },
    { key: 'print', icon: 'print', label: t('printing.drawing'), onSelect: onPrint, disabled: printBusy }
  ];
  const quickExportActions = exportActions.filter((action) => action.key === 'print');
  const saveAction: MoreMenuAction = {
    key: 'save',
    icon: 'save',
    label: saveReadOnly
      ? t('tenancy.viewOnly')
      : saveBusy
        ? t('persistencePanel.saving')
        : t('workspace.actionBar.save'),
    onSelect: onSave,
    disabled: saveBusy || saveReadOnly
  };
  const fileOperations: readonly MoreMenuAction[] = [
    ...(saveVisible ? [saveAction] : []),
    ...importActions.map((action) => ({ ...action, key: `import-${action.key}` })),
    ...(hasContent ? exportActions.map((action) => ({ ...action, key: `export-${action.key}` })) : [])
  ];
  const selectAllAction: MoreMenuAction = {
    key: 'selectAll',
    icon: 'selectAll',
    label: t('editorToolPalette.selection.selectAll'),
    disabled: !hasContent,
    onSelect: () =>
      doc.setSelection({
        atomIds: document.structure.graph.nodes.map((node) => node.id),
        bondIds: document.structure.graph.edges.map((edge) => edge.id),
        annotationIds: document.annotations.map((annotation) => annotation.id)
      })
  };
  const selectionActions: readonly MoreMenuAction[] = [
    {
      key: 'attachCallout',
      icon: 'link',
      label: t('editorToolPalette.selection.attachCallout'),
      onSelect: doc.attachSelectedCallouts,
      disabled: !doc.canAttachCallouts
    },
    {
      key: 'detachCallout',
      icon: 'link',
      label: t('editorToolPalette.selection.detachCallout'),
      onSelect: doc.detachSelectedCallouts,
      disabled: !doc.canDetachCallouts
    },
    {
      key: 'cut',
      icon: 'cut',
      label: t('editorToolPalette.selection.cut'),
      onSelect: doc.cutSelection,
      disabled: !doc.canCopySelection
    },
    {
      key: 'copy',
      icon: 'copy',
      label: t('editorToolPalette.selection.copy'),
      onSelect: doc.copySelection,
      disabled: !doc.canCopySelection
    },
    {
      key: 'paste',
      icon: 'paste',
      label: t('editorToolPalette.selection.paste'),
      onSelect: doc.pasteClipboard,
      disabled: !doc.hasClipboard
    },
    {
      key: 'delete',
      icon: 'delete',
      label: t(
        doc.singleAtomId
          ? 'editorToolPalette.delete.atom'
          : doc.singleBondId
            ? 'editorToolPalette.delete.bond'
            : 'editorToolPalette.delete.selection'
      ),
      onSelect: doc.deleteSelection,
      disabled: !hasSelection(doc.selection)
    }
  ];

  const visibleSelectionActions = selectionActions.filter((action) => !action.disabled);

  // #1071 -- only when its own toolbar button has been squeezed out; it was listed twice.
  const actions: readonly MoreMenuAction[] =
    hasContent && hiddenGroups.has('quickClear')
      ? [
          {
            key: 'clear',
            icon: 'inkEraser',
            label: t('drawingToolNav.clearDrawing'),
            onSelect: clearDrawing,
            disabled: !hasContent
          }
        ]
      : [];
  // #807 R4 -- the app's own groups, offered in ⋯ when the row has no room for them.
  const closeMore = () => onMoreOpenChange(false);
  const overflow = (
    <>
      {hasContent && hiddenGroups.has('quickExport') && !hiddenGroups.has('operations') ? (
        <div role="group" aria-label={t('workspace.actionBar.quickExport')} className="workspace-more-group">
          {quickExportActions.map(({ key, icon, label, onSelect, disabled }) => (
            <button
              key={key}
              type="button"
              role="menuitem"
              className="workspace-more-item workspace-more-action"
              disabled={disabled}
              onClick={() => {
                closeMore();
                onSelect();
              }}
            >
              <Icon name={icon} size="1.1rem" />
              <span>{label}</span>
            </button>
          ))}
        </div>
      ) : null}
      {hiddenGroups.has('operations') ? (
        <div role="group" aria-label={t('workspace.actionBar.fileOperations')} className="workspace-more-group">
          {fileOperations.map(({ key, icon, label, onSelect, disabled }) => (
            <button
              key={key}
              type="button"
              role="menuitem"
              className="workspace-more-item workspace-more-action"
              disabled={disabled}
              onClick={() => {
                closeMore();
                onSelect();
              }}
            >
              <Icon name={icon} size="1.1rem" />
              <span>{label}</span>
            </button>
          ))}
        </div>
      ) : null}
      {hiddenGroups.has('file') ? (
        <button
          type="button"
          role="menuitem"
          className="workspace-more-item workspace-more-action"
          aria-haspopup="dialog"
          onClick={() => {
            closeMore();
            onOpenFile();
          }}
        >
          <Icon name="folder" size="1.1rem" />
          <span>{t('workspace.fileDrawer.open')}</span>
        </button>
      ) : null}
      {hiddenGroups.has('selection') ? (
        <div role="group" aria-label={t('workspace.actionBar.selectionGroup')} className="workspace-more-group">
          {visibleSelectionActions.map(({ key, icon, label, onSelect, disabled }) => (
            <button
              key={key}
              type="button"
              role="menuitem"
              className="workspace-more-item workspace-more-action"
              disabled={disabled}
              onClick={() => {
                closeMore();
                onSelect();
              }}
            >
              <Icon name={icon} size="1.1rem" />
              <span>{label}</span>
            </button>
          ))}
        </div>
      ) : null}
      <div role="group" aria-label={t('drawingToolNav.pointerModes')} className="workspace-more-group">
        {(
          [
            ['draw', 'edit', t('drawingToolNav.draw')],
            ['select', 'arrowSelectorTool', t('drawingToolNav.selectAndEdit')],
            ['selectAll', 'selectAll', selectAllAction.label],
            ['pan', 'panTool', t('paperViewportControls.pan')]
          ] as const
        )
          .filter(([mode]) =>
            mode === 'pan' ? hiddenGroups.has('pan') : hiddenGroups.has('modes') && (mode === 'draw' || hasContent)
          )
          .map(([mode, icon, label]) => (
            <button
              key={mode}
              type="button"
              role={mode === 'selectAll' ? 'menuitem' : 'menuitemradio'}
              aria-checked={mode === 'selectAll' ? undefined : tool === mode}
              className="workspace-more-item workspace-more-action"
              disabled={mode === 'select' && !hasContent}
              onClick={() => {
                if (mode === 'selectAll') selectAllAction.onSelect();
                else setTool(mode);
                closeMore();
              }}
            >
              <Icon name={icon} size="1.1rem" />
              <span>{label}</span>
            </button>
          ))}
      </div>
      <AtomContextControls
        doc={doc}
        partialCharges={partialCharges}
        hiddenGroups={hiddenGroups}
        menu
        onPanel={(panel) => {
          const anchor = globalThis.document.querySelector<HTMLElement>('[data-testid="more-menu-trigger"]');
          closeMore();
          openAtomPanel(panel, anchor);
        }}
        onAction={closeMore}
      />
      <SelectionContextControls
        doc={doc}
        context={context}
        hiddenGroups={hiddenGroups}
        menu
        onAppearance={(kind) => {
          const anchor = globalThis.document.querySelector<HTMLElement>('[data-testid="more-menu-trigger"]');
          closeMore();
          openAppearance(kind, anchor);
        }}
        onAction={closeMore}
      />
      <SelectionArrangeControls doc={doc} hiddenGroups={hiddenGroups} menu onAction={closeMore} />
      {hasNodes && hiddenGroups.has('layout') ? (
        <button
          type="button"
          role="menuitem"
          className="workspace-more-item workspace-more-action"
          disabled={!hasNodes || autoLayoutBusy}
          onClick={() => {
            closeMore();
            onAutoLayout();
          }}
        >
          <Icon name="polyline" size="1.1rem" />
          <span>{t('workspace.actionBar.autoLayout')}</span>
        </button>
      ) : null}
    </>
  );

  return (
    <div
      className="workspace-action-bar"
      data-testid="editor-action-bar"
      role="toolbar"
      aria-label={t('workspace.actionBar.label')}
    >
      <div
        className="workspace-action-group"
        role="group"
        aria-label={t('workspace.actionBar.fileGroup')}
        data-toolbar-group="file"
        hidden={hiddenGroups.has('file')}
      >
        <button
          ref={fileButtonRef}
          type="button"
          className="icon-button"
          onClick={onOpenFile}
          aria-haspopup="dialog"
          aria-label={t('workspace.fileDrawer.open')}
          title={t('workspace.fileDrawer.open')}
          data-testid="file-drawer-trigger"
        >
          <Icon name="folder" />
        </button>
      </div>

      <div
        className="workspace-action-group"
        role="group"
        aria-label={t('workspace.actionBar.fileOperations')}
        data-toolbar-group="operations"
        hidden={hiddenGroups.has('operations')}
      >
        {saveVisible ? (
          <button
            type="button"
            className="icon-button"
            aria-label={saveAction.label}
            title={saveAction.label}
            disabled={saveAction.disabled}
            onClick={onSave}
          >
            <Icon name="save" />
          </button>
        ) : null}
        <ToolbarMenu label={t('workspace.actionBar.import')} icon="importContent" actions={importActions} />
        {hasContent ? (
          <ToolbarMenu label={t('workspace.actionBar.export')} icon="exportContent" actions={exportActions} />
        ) : null}
      </div>
      {hasContent ? (
        <div
          className="workspace-action-group"
          role="group"
          aria-label={t('workspace.actionBar.quickExport')}
          data-toolbar-group="quickExport"
          hidden={hiddenGroups.has('quickExport')}
        >
          {quickExportActions.map(({ key, icon, label, onSelect, disabled }) => (
            <button
              key={key}
              type="button"
              className="icon-button"
              aria-label={label}
              title={label}
              disabled={disabled}
              onClick={onSelect}
            >
              <Icon name={icon} />
            </button>
          ))}
        </div>
      ) : null}
      <div className="workspace-action-group" role="group" aria-label={t('drawingToolNav.historyGroupAriaLabel')}>
        {history.past.length > 0 ? (
          <button
            className="icon-button"
            type="button"
            aria-label={t('drawingToolNav.undo')}
            title={t('drawingToolNav.undo')}
            onClick={undo}
            disabled={history.past.length === 0}
          >
            <Icon name="undo" />
          </button>
        ) : null}
        {history.future.length > 0 ? (
          <button
            className="icon-button"
            type="button"
            aria-label={t('drawingToolNav.redo')}
            title={t('drawingToolNav.redo')}
            onClick={redo}
            disabled={history.future.length === 0}
          >
            <Icon name="redo" />
          </button>
        ) : null}
      </div>

      {visibleSelectionActions.length > 0 ? (
        <div
          className="workspace-action-group"
          role="group"
          aria-label={t('workspace.actionBar.selectionGroup')}
          data-toolbar-group="selection"
          hidden={hiddenGroups.has('selection')}
        >
          {visibleSelectionActions.map(({ key, icon, label, onSelect, disabled }) => (
            <button
              key={key}
              type="button"
              className={key === 'delete' ? 'icon-button danger' : 'icon-button'}
              aria-label={label}
              title={label}
              disabled={disabled}
              onClick={onSelect}
            >
              <Icon name={icon} />
            </button>
          ))}
        </div>
      ) : null}
      <div className="workspace-action-group workspace-action-group--modes">
        <PointerModeToggle
          tool={tool}
          setTool={setTool}
          hasSelectableContent={hasContent}
          onSelectAll={selectAllAction.onSelect}
          hideUnavailable
          selectHidden={hiddenGroups.has('modes')}
          panHidden={hiddenGroups.has('pan')}
        />
        {viewControls}
      </div>

      <div
        className="workspace-action-group"
        role="group"
        aria-label={t('workspace.actionBar.layoutGroup')}
        data-toolbar-group="layout"
        hidden={!hasNodes || hiddenGroups.has('layout')}
      >
        <button
          className="icon-button"
          type="button"
          aria-label={t('workspace.actionBar.autoLayout')}
          title={t('workspace.actionBar.autoLayout')}
          onClick={onAutoLayout}
          disabled={!hasNodes || autoLayoutBusy}
        >
          <Icon name="polyline" />
        </button>
      </div>

      {hasContent ? (
        <div
          className="workspace-action-group"
          role="group"
          aria-label={t('drawingToolNav.clearDrawing')}
          data-toolbar-group="quickClear"
          hidden={hiddenGroups.has('quickClear')}
        >
          <button
            type="button"
            className="icon-button"
            aria-label={t('drawingToolNav.clearDrawing')}
            title={t('drawingToolNav.clearDrawing')}
            onClick={clearDrawing}
          >
            <Icon name="inkEraser" />
          </button>
        </div>
      ) : null}

      {/* #1078 (owner) -- the always-there controls (modes, Pan, zoom, fit, page, Auto layout,
          Clear) lead the row; what a selection brings up comes after them, so the frequent ones
          never shift when it appears. */}
      <AtomContextControls
        doc={doc}
        partialCharges={partialCharges}
        hiddenGroups={hiddenGroups}
        onPanel={openAtomPanel}
      />
      <SelectionContextControls doc={doc} context={context} hiddenGroups={hiddenGroups} onAppearance={openAppearance} />
      <SelectionArrangeControls doc={doc} hiddenGroups={hiddenGroups} />
      {hasContent || hiddenGroups.size > 0 ? (
        <div className="workspace-action-group" role="group" aria-label={t('workspace.actionBar.moreGroup')}>
          <MoreMenu
            actions={actions}
            items={[]}
            overflow={overflow}
            overflowSlotRef={overflowSlotRef}
            open={moreOpen}
            onOpenChange={onMoreOpenChange}
            onTrigger={onMoreTrigger}
          />
        </div>
      ) : null}
      {openAtom === 'element' ? (
        <PeriodicTablePanel
          open
          armedZ={doc.selectedAtom?.atomicNumber ?? null}
          onPick={(atomicNumber) => {
            doc.updateSelectedAtom(atomicNumber);
            setAtomPanel(null);
          }}
          onClose={() => {
            setAtomPanel(null);
            atomAnchor.current?.focus();
          }}
        />
      ) : null}
      {openAtom === 'isotope' || openAtom === 'radical'
        ? createPortal(
            <AtomPropertyPopover
              key={`${openAtom}:${doc.singleAtomId}`}
              doc={doc}
              panel={openAtom}
              anchorRef={atomAnchor}
              onClose={() => setAtomPanel(null)}
            />,
            globalThis.document.body
          )
        : null}
      {appearance && (appearance === 'color' ? context.hasStructure : context.hasBonds)
        ? createPortal(
            <SelectionAppearancePopover
              key={`${appearance}:${context.key}`}
              doc={doc}
              kind={appearance}
              anchorRef={appearanceAnchor}
              onClose={() => setAppearance(null)}
            />,
            globalThis.document.body
          )
        : null}
    </div>
  );
}
